import React, { useEffect, useMemo, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import { Box, Button, Input, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { theme } from "styles/theme";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import ListButton from "../../components/common/ListButton";
import { SearchIconSvg } from "../../static/image/common/SvgAssets";
import Fuse, { FuseResult } from "fuse.js";
import { CoachingFaq } from "../../api/coachingFaqApi";
import faqData from "../../data/faqData.json";
import { expandQueryWithSynonyms } from "../../data/synonyms";
import { getConditionalFaqList } from "../../utils/faqUtils";
import BackButton from "../../components/common/BackButton";
import FullPageContainer from "../container/FullPageContainer";
import useUnifiedNavigate from "../../hooks/useUnifiedNavigate";
import useSafeAreaColor from "../../hooks/useSafeAreaColor";
import { useChannelTalkContext } from "../../contexts/ChannelTalkContext";
import useGoBack from "../../hooks/useGoBack";
import useBottomSafePadding from "../../hooks/useBottomSafePadding";

const getChosung = (str: string) => {
  const CHO_HANGUL = [
    "ㄱ",
    "ㄲ",
    "ㄴ",
    "ㄷ",
    "ㄸ",
    "ㄹ",
    "ㅁ",
    "ㅂ",
    "ㅃ",
    "ㅅ",
    "ㅆ",
    "ㅇ",
    "ㅈ",
    "ㅉ",
    "ㅊ",
    "ㅋ",
    "ㅌ",
    "ㅍ",
    "ㅎ",
  ];

  let result = "";
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i) - 44032;
    if (code > -1 && code < 11172) {
      result += CHO_HANGUL[Math.floor(code / 588)];
    } else {
      result += str.charAt(i);
    }
  }
  return result;
};

/**
 * Match types for different kinds of matches in search
 *
 * The match types are ordered by priority (lower score is higher priority):
 * - EXACT_KEYWORD: The keyword exactly matches the search term (score: 0)
 * - EXACT_CONTENT: The search term appears exactly in question or answer (score: 0.05)
 * - KEYWORD_CONTAINS: The keyword contains the search term (score: 0.1)
 * - CONTENT_CONTAINS: The question or answer contains the search term (score: 0.15)
 * - TERM_CONTAINS: The search term contains the keyword (score: 0.2)
 */
type MatchType =
  | "EXACT_KEYWORD"
  | "EXACT_CONTENT"
  | "KEYWORD_CONTAINS"
  | "CONTENT_CONTAINS"
  | "TERM_CONTAINS";

interface SearchMatch {
  faq: CoachingFaq;
  matchType: MatchType;
}

const findFaqMatches = (
  searchTerm: string,
  faqList: CoachingFaq[]
): SearchMatch[] => {
  // Convert search term to lowercase for case-insensitive matching
  const lowerTerm = searchTerm.toLowerCase();

  // Array to store matches with their match types
  const matches: SearchMatch[] = [];

  // Check each FAQ for keyword and content matches
  faqList.forEach((faq) => {
    const lowerQuestion = faq.question.toLowerCase();
    const lowerAnswer = faq.answer.toLowerCase();

    // Check for content matches first
    if (lowerQuestion === lowerTerm || lowerAnswer === lowerTerm) {
      matches.push({ faq, matchType: "EXACT_CONTENT" });
    } else if (
      lowerQuestion.includes(lowerTerm) ||
      lowerAnswer.includes(lowerTerm)
    ) {
      matches.push({ faq, matchType: "CONTENT_CONTAINS" });
    }

    // Then check for keyword matches if keywords exist
    if (faq.keywords && faq.keywords.length > 0) {
      faq.keywords.forEach((keyword) => {
        const lowerKeyword = keyword.toLowerCase();

        // Check for exact match first (highest priority)
        if (lowerKeyword === lowerTerm) {
          matches.push({ faq, matchType: "EXACT_KEYWORD" });
        }
        // Check if keyword contains the search term
        else if (lowerKeyword.includes(lowerTerm)) {
          matches.push({ faq, matchType: "KEYWORD_CONTAINS" });
        }
        // Check if search term contains the keyword
        else if (lowerTerm.includes(lowerKeyword)) {
          matches.push({ faq, matchType: "TERM_CONTAINS" });
        }
      });
    }
  });

  // Remove duplicates, keeping the highest priority match for each FAQ
  const uniqueMatches = Array.from(
    new Map(
      // Sort by match type priority before creating the Map to ensure highest priority is kept
      matches
        .sort((a, b) => {
          const priorityOrder = {
            EXACT_KEYWORD: 0,
            EXACT_CONTENT: 1,
            KEYWORD_CONTAINS: 2,
            CONTENT_CONTAINS: 3,
            TERM_CONTAINS: 4,
          };
          return priorityOrder[a.matchType] - priorityOrder[b.matchType];
        })
        .map((match) => [match.faq.faqId, match])
    ).values()
  );

  return uniqueMatches;
};

export default function SupportFaqListPage() {
  const user = useRecoilValue(userAtom);
  const { openChat } = useChannelTalkContext();
  const [searchTerm, setSearchTerm] = useState("");
  const unifiedNavigate = useUnifiedNavigate();
  const goBack = useGoBack();
  // isFullPage 의 안쪽 Stack 이 height: 100vh 로 되어 FullPageContainer 의 pb=env() 가
  // 무력화됨. scroll Stack 자체에 pb 를 주어 마지막 list 가 home indicator 에
  // 붙지 않도록 보장.
  const innerScrollPb = useBottomSafePadding("16px");

  const isNoPaybackPolicy =
    user?.refundPolicyType === undefined ||
    user?.refundPolicyType === null ||
    user?.refundPolicyType === "b" ||
    user?.refundPolicyType === "c";

  const isFullPage = window.location.pathname === "/support/faq";
  const backgroundColor = theme.vars.palette.Background.Level1;
  useSafeAreaColor(backgroundColor);

  // Get the original FAQ list and filter out hidden items
  const originalFaqList = (faqData as CoachingFaq[]).filter(
    (e) => e.hidden !== true
  );

  // Apply conditional content based on user properties
  const faqList = useMemo(() => {
    const list = getConditionalFaqList(originalFaqList, user);
    if (isNoPaybackPolicy) {
      return list.filter((e) => e.faqId !== 102);
    }
    return list;
  }, [originalFaqList, user, isNoPaybackPolicy]);

  const fuseOptions = {
    includeScore: true,
    threshold: 0.2,
    distance: 100,
    minMatchCharLength: 1,
    keys: ["question", "answer", "questionChosung", "answerChosung"],
  };

  const fuse = useMemo(() => {
    if (!faqList) return null;

    const faqItemsWithChosung = faqList.map((item) => ({
      ...item,
      questionChosung: getChosung(item.question),
      answerChosung: getChosung(item.answer),
    }));

    return new Fuse(faqItemsWithChosung, fuseOptions);
  }, [faqList]);

  const filteredFAQs: CoachingFaq[] = useMemo(() => {
    if (!faqList) return [];
    if (!searchTerm || !fuse) return faqList;

    // Step 1: Check if the search term matches any FAQ's keywords or content
    // Find FAQs whose keywords or content match the search term with match type information
    const directMatches = findFaqMatches(searchTerm, faqList);

    // Step 2: Perform the regular fuzzy search
    // Expand search term with synonyms
    const expandedTerms = expandQueryWithSynonyms(searchTerm);

    // Search for each expanded term and combine results
    let allResults: FuseResult<any>[] = [];

    expandedTerms.forEach((term) => {
      const results = fuse.search(term);
      allResults = [...allResults, ...results];
    });

    // Step 3: Combine direct matches with fuzzy search results
    // Convert direct matches to FuseResult format with scores based on match type
    const directResults: FuseResult<any>[] = directMatches.map((match) => {
      // Assign scores based on match type (lower is better)
      let score = 0; // Default for EXACT_KEYWORD

      switch (match.matchType) {
        case "EXACT_CONTENT":
          score = 0.05;
          break;
        case "KEYWORD_CONTAINS":
          score = 0.1;
          break;
        case "CONTENT_CONTAINS":
          score = 0.15;
          break;
        case "TERM_CONTAINS":
          score = 0.2;
          break;
      }

      return {
        item: match.faq,
        refIndex: -1, // Not used in our case
        score: score,
      };
    });

    // Combine direct matches with fuzzy search results
    allResults = [...directResults, ...allResults];

    // Remove duplicates by faqId, keeping the one with the lowest score (highest priority)
    const uniqueResults = Array.from(
      new Map(
        // Sort by score before creating the Map to ensure lowest score is kept
        allResults
          .sort((a, b) => (a.score || 1) - (b.score || 1))
          .map((item) => [item.item.faqId, item])
      ).values()
    );

    // Sort by score (lower is better)
    uniqueResults.sort((a, b) => (a.score || 1) - (b.score || 1));

    return uniqueResults.map((r) => r.item);
  }, [searchTerm, fuse, faqList]);

  const navigateToDetail = (faqId: number) => {
    if (!isFullPage) {
      unifiedNavigate(`/support-faq?fid=${faqId}`, {
        type: "card",
        animation: "default",
        gestureEnabled: true,
      });
    } else {
      unifiedNavigate(`/support/faq/detail?fid=${faqId}`, {
        type: "card",
        animation: "default",
        gestureEnabled: true,
      });
    }
  };

  const child = (
    <>
      <Box
        sx={{
          width: "100%",
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: backgroundColor,
          px: "20px",
          py: "8px",
        }}
      >
        <Input
          placeholder="검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startDecorator={
            <SearchIconSvg color={theme.vars.palette.Content.Weak} />
          }
          sx={{
            backgroundColor: theme.vars.palette.Surface.Backward,
            px: "20px",
            py: "12px",
            width: "100%",
            borderRadius: "16px",
            boxShadow: "none",
            "--joy-focus-thickness": "0px",
            "--variant-borderWidth": "0px",
            "--Input-placeholderColor": theme.vars.palette.Content.Weak,
            "--Input-color": theme.vars.palette.Content.Strong,
            "--Input-placeholderOpacity": 1,
            "--Input-placeholderFontWeight": 500,
            "--Input-placeholderFontSize": "16px",
            "--Input-decoratorChildHeight": "auto",
            "--Input-gap": "8px",
          }}
        />
      </Box>
      <Stack
        spacing={"8px"}
        sx={{
          px: "20px",
          pb: isFullPage ? "20px" : innerScrollPb,
          height: filteredFAQs.length === 0 ? "calc(100vh - 200px)" : "auto",
        }}
      >
        {filteredFAQs.length === 0 && (
          <>
            <Stack
              alignItems={"center"}
              justifyContent={"center"}
              spacing={2}
              sx={{ height: "100%" }}
            >
              <Typography
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {"검색 결과가 없어요.\n" +
                  "도움이 필요하시면 언제든 문의주세요."}
              </Typography>
              <Button
                variant={"Soft"}
                size={"md"}
                sx={{
                  width: "200px",
                  height: "46px",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
                onClick={() => {
                  openChat();
                }}
              >
                {"직접 문의하기"}
              </Button>
            </Stack>
          </>
        )}

        {filteredFAQs.map((faq) => (
          <Box key={faq.faqId}>
            <ListButton
              variant={"Plain"}
              color={"neutral"}
              text={faq.question}
              onClick={() => navigateToDetail(faq.faqId)}
            />
          </Box>
        ))}
      </Stack>
    </>
  );
  return isFullPage ? (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100vw",
          mx: "auto",
          height: "100vh",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: `60px`, // 헤더 높이 설정
            px: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BackButton onBack={goBack} />
            <Typography
              variant="Body"
              sx={{
                px: 0,
                fontColor: theme.vars.palette.Content.Strong,
                fontWeight: 700,
              }}
            >
              {"고객센터"}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            pt: 0,
            pb: innerScrollPb,
            position: "relative",
          }}
        >
          {child}
        </Stack>
      </Stack>
    </FullPageContainer>
  ) : (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`support_${user?.userId}`}
          taskKey={`support-${user?.userId}`}
          title={"고객센터"}
        />
      </Box>
      <ContentTemplateV2 fullBleed disableBottomSafeArea>
        {child}
      </ContentTemplateV2>
    </>
  );
}
